/**
 * @file Segmented.tsx
 * @description Переключатель-сегмент «[Обычное][Полужирное]» для настроек
 * вида сайта (phase19.16 Р1; uiux-design-r1 §1, §2): выбор одного варианта
 * из 2–4 коротких.
 *
 * Не вкладки (SC45) и не кнопки действий: группа радиокнопок
 * (`role="radiogroup"`), выбранный вариант — на мягкой подложке. Высота —
 * как у элементов управления темы (48 до 1024, 44 с 1024); на узком экране
 * ряд переносится, а не вылезает за край.
 *
 * `fill` (v0.19.65, QA «SEO-техника»): сегменты равной ширины в одну строку
 * на всю ширину, шрифт 13 и меньший отступ — длинная подпись переносится
 * внутри своего сегмента, а не уводит выбранный вариант на вторую строку.
 */

import type { KeyboardEvent, ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Подпись для скринридера, если `label` — не текст. */
  ariaLabel?: string;
  disabled?: boolean;
}

interface Props<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  "aria-label": string;
  className?: string;
  /** Равные сегменты в одну строку на всю ширину (360: «Как на сайте · Показывать · Скрыть»). */
  fill?: boolean;
}

export function Segmented<T extends string>({ options, value, onChange, className, fill = false, "aria-label": ariaLabel }: Props<T>) {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const enabled = options.filter((o) => !o.disabled);
    const index = enabled.findIndex((o) => o.value === value);
    const next = enabled[(index + step + enabled.length) % enabled.length];
    if (next) onChange(next.value);
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn("max-w-full gap-1 rounded-xl bg-surface-low p-1", fill ? "flex w-full flex-nowrap" : "inline-flex flex-wrap", className)}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.ariaLabel}
            tabIndex={active ? 0 : -1}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex min-h-[var(--control-h,3rem)] items-center justify-center gap-2 rounded-lg font-medium outline-none transition-colors",
              fill
                ? "min-w-0 flex-1 px-1.5 text-center text-[0.8125rem] leading-tight"
                : "min-w-12 px-3 text-[length:var(--fs-control,0.875rem)]",
              "focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
              active
                ? "bg-surface-lowest text-on-surface [box-shadow:inset_0_0_0_1px_var(--outline-variant)]"
                : "text-on-surface-variant hover:text-on-surface",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
